// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "La mia azienda"
// Centro assistenza → guida per amministratori (HACCP).
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Etichette dal codice.
//
// Riferimenti nel codice, per chi dovra' aggiornarla:
//   Schermata ...... App.js, ProfiloAzienda — titolo "La mia azienda",
//                    sottotitolo "Dati anagrafici, contatti e logo della tua
//                    struttura". Sezioni (sectionHead):
//                    · "Dati anagrafici" — Ragione sociale *, Indirizzo, Città
//                    · "Contatti" — "Recapiti usati in bolle e documenti":
//                      Email, Telefono
//                    · "Dati fiscali" — "Necessari per emettere la fattura
//                      elettronica via Sistema di Interscambio (SDI)":
//                      Partita IVA ("11 cifre"), Codice fiscale ("16 caratteri
//                      (o 11 cifre)"), CAP, Provincia ("es. MO"), Nazione
//                      ("IT"), Codice destinatario SDI ("7 caratteri"), PEC
//                    · "Logo aziendale" — "Appare su stampe, intestazioni e
//                      bolle"; "Formati consigliati: PNG o SVG su sfondo
//                      trasparente, max 1 MB"; "Carica logo"/"Cambia logo"
//                    · "Abbonamento e fatturazione" — Piano + stato (Attivo
//                      verde / Prova gratuita blu / Sola lettura arancio /
//                      Cancellato rosso); in prova: "Scade il ..."; pulsanti
//                      "Gestisci abbonamento" / "Attiva un abbonamento"
//                    · "Cartello QR per la produzione" — "Stampa un A4 con il
//                      QR che porta direttamente alla pagina di accesso della
//                      tua azienda"; "Anteprima cartello"; "Da plastificare e
//                      appendere in produzione"
//                    · "Salva modifiche"
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout,
  MockFrame, Schermo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'a0', label: 'La carta intestata del sistema' },
  { id: 'a1', label: '1. Dati, contatti e dati fiscali' },
  { id: 'a2', label: '2. Il logo' },
  { id: 'a3', label: '3. L’abbonamento' },
  { id: 'a4', label: '4. Il cartello QR' },
  { id: 'a5', label: 'Le trappole' },
  { id: 'a6', label: 'Domande frequenti' },
];

// ─── Pezzi di schermata ──────────────────────────────────────────────────────

const ChipFinto = ({ children, tono = 'neutro' }) => {
  const c = {
    verde:   { fg: LT.verde,   bg: LT.verdeSoft },
    blu:     { fg: LT.blu,     bg: LT.bluSoft },
    arancio: { fg: LT.arancio, bg: LT.arancioSoft },
    rosso:   { fg: LT.rosso,   bg: LT.rossoSoft },
    neutro:  { fg: LT.n700,    bg: LT.n100 },
  }[tono];
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 5,
      padding: '4px 11px', borderRadius: 999, background: c.bg, color: c.fg,
      fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
};

export default function MiaAzienda() {
  return (
    <GuidaLayout
      titolo="La mia azienda"
      sottotitolo="I dati, il logo e l'abbonamento: la schermata che si compila una volta bene — e da quel momento firma la testata di ogni stampa, bolla e documento."
      durata="~ 7 minuti"
      destinatario="Per amministratori"
      sezioni={SEZIONI}
    >

      {/* a0 */}
      <H id="a0">La carta intestata del sistema</H>

      <P>
        <Code>La mia azienda</Code> è la scheda anagrafica della vostra struttura: ragione
        sociale, indirizzo, contatti, logo. Non è burocrazia interna — è ciò che finisce{' '}
        <Strong>in testa a tutto quello che il sistema produce</Strong>: il Registro
        monitoraggi stampato, le bolle, i documenti. Compilata bene una volta, lavora da
        sola per sempre; compilata a metà, si vede su ogni foglio che esce.
      </P>

      {/* a1 */}
      <H id="a1" cat="formazione">1. Dati, contatti e dati fiscali</H>

      <P>
        Le prime due sezioni sono immediate: <Strong>Dati anagrafici</Strong> (l'unico
        campo obbligatorio è la Ragione sociale) e <Strong>Contatti</Strong> — email e
        telefono, che come dice la schermata stessa sono i recapiti usati in bolle e
        documenti: chi riceve una vostra bolla vede quelli.
      </P>
      <P>
        La sezione <Strong>Dati fiscali</Strong> serve a una cosa sola, detta chiara:
        emettervi la <Strong>fattura elettronica</Strong> dell'abbonamento via SDI.
        Partita IVA, codice fiscale, CAP, provincia, e la coppia che decide dove arriva la
        fattura: <Strong>Codice destinatario SDI</Strong> (7 caratteri) oppure la{' '}
        <Strong>PEC</Strong>. Ne basta una delle due — chiedete al vostro commercialista
        quale usa.
      </P>

      {/* a2 */}
      <H id="a2" cat="produzione">2. Il logo</H>

      <MockFrame
        url="app.labthree.it — La mia azienda"
        didascalia="Il consiglio scritto sotto il pulsante è quello giusto: PNG o SVG su sfondo trasparente. Un logo su fondo bianco dentro una testata colorata si vede, e non in senso buono."
      >
        <Schermo>
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`,
            borderRadius: LT.r.lg, padding: 16, maxWidth: 470, margin: '0 auto',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: LT.r.md,
                background: LT.arancioSoft, display: 'flex',
                alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <Ico d={I.camera} size={18} stroke={LT.arancio} />
              </div>
              <div>
                <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>Logo aziendale</div>
                <div style={{ fontSize: 12, color: LT.n500 }}>Appare su stampe, intestazioni e bolle</div>
              </div>
            </div>
            <div style={{
              border: `1.5px dashed ${LT.n300}`, borderRadius: LT.r.md,
              padding: '18px 12px', textAlign: 'center', marginBottom: 10,
              fontSize: 12.5, color: LT.n500,
            }}>
              Formati consigliati: PNG o SVG su sfondo trasparente, max 1 MB.
            </div>
            <div style={{
              padding: '9px 14px', borderRadius: LT.r.md, textAlign: 'center',
              border: `1px solid ${LT.n300}`, fontSize: 13, fontWeight: 700, color: LT.n700,
            }}>Carica logo</div>
          </div>
        </Schermo>
      </MockFrame>

      <P>
        Il logo caricato qui compare sulle stampe del registro, sulle bolle e nelle
        intestazioni. Il caricamento è immediato — non serve premere Salva — e{' '}
        <Tasto>Cambia logo</Tasto> lo sostituisce quando serve: le stampe si generano al
        momento, quindi dalla sostituzione in poi esce sempre quello nuovo.
      </P>

      {/* a3 */}
      <H id="a3" cat="formazione">3. L'abbonamento</H>

      <P>
        La sezione <Strong>Abbonamento e fatturazione</Strong> mostra il piano e il suo
        stato: <ChipFinto tono="blu">Prova gratuita</ChipFinto> con la data di scadenza
        scritta accanto, <ChipFinto tono="verde">Attivo</ChipFinto>, oppure{' '}
        <ChipFinto tono="arancio">Sola lettura</ChipFinto> — che è cosa succede quando
        l'abbonamento scade: <Strong>non si perde niente</Strong>, i dati e le stampe
        restano consultabili, ma le nuove registrazioni si fermano finché non si riattiva.
      </P>
      <P>
        <Tasto>Gestisci abbonamento</Tasto> apre il portale di pagamento: fatture, carta,
        cambio piano, disdetta. In prova, il pulsante è{' '}
        <Tasto>Attiva un abbonamento</Tasto> — attivabile in qualsiasi momento senza
        aspettare la scadenza, e senza interruzioni di servizio.
      </P>

      {/* a4 */}
      <H id="a4" cat="produzione">4. Il cartello QR</H>

      <P>
        In fondo alla pagina, <Strong>Cartello QR per la produzione</Strong>:{' '}
        <Tasto>Anteprima cartello</Tasto> genera un A4 con il codice QR che porta
        direttamente alla pagina di accesso della vostra azienda. Come dice la schermata:{' '}
        <em>da plastificare e appendere in produzione</em> — l'operatore inquadra col
        telefono, mette il PIN ed è dentro, senza digitare indirizzi.
      </P>

      <Callout kind="tip" titolo="Dove appenderlo">
        Vicino a dove si fanno i controlli, non in ufficio: accanto alle celle, in
        laboratorio. È il pezzo che rende naturale registrare dal telefono davanti alla
        cella — insieme all'aggiunta di LabThree alla schermata Home.
      </Callout>

      {/* a5 */}
      <H id="a5">Le trappole</H>

      <Callout kind="warn" titolo="Il Salva che non copre il logo — e viceversa">
        I dati (anagrafici, contatti, fiscali) si salvano col pulsante{' '}
        <Tasto>Salva modifiche</Tasto> in fondo; il logo invece si carica da sé, subito.
        Se compilate i campi e chiudete senza Salva, i dati si perdono — anche se il logo,
        caricato un attimo prima, è rimasto.
      </Callout>

      <Callout kind="warn" titolo="Dati fiscali a metà">
        Senza partita IVA e senza né codice SDI né PEC, la fattura elettronica
        dell'abbonamento non ha dove arrivare. Due minuti col commercialista al primo
        accesso evitano il rimpallo al momento del rinnovo.
      </Callout>

      <Callout kind="warn" titolo="Il logo fotografato">
        La foto del biglietto da visita o un JPG su fondo bianco funzionano, ma su ogni
        stampa si porteranno dietro il rettangolo bianco e la sgranatura. Il file giusto
        ce l'ha chi vi ha fatto il logo: PNG o SVG su sfondo trasparente — chiedetelo una
        volta e siete a posto per sempre.
      </Callout>

      {/* a6 */}
      <H id="a6">Domande frequenti</H>

      <div style={{ marginTop: 8 }}>
        <FAQ q="Chi può modificare questi dati?">
          Gli amministratori. Gli operatori non vedono questa sezione — e i dati
          dell'azienda compaiono loro solo dove devono: sulle stampe e nelle intestazioni.
        </FAQ>

        <FAQ q="Cosa succede esattamente in «Sola lettura»?">
          Si può entrare, consultare e stampare tutto: lo storico resta vostro. Non si
          possono fare nuove registrazioni finché l'abbonamento non riparte. È il modo di
          scadere senza perdere niente — e senza buchi misteriosi nel registro: il periodo
          fermo è documentabile.
        </FAQ>

        <FAQ q="Cambiare il logo cambia anche le stampe già fatte?">
          I PDF già salvati o stampati restano come sono — sono fogli, ormai. Tutto ciò
          che si genera da adesso in poi usa il logo nuovo, perché le stampe si
          costruiscono al momento.
        </FAQ>

        <FAQ q="Ho perso il cartello QR: ne stampo un altro?">
          Sì, da qui, quante volte serve: <Tasto>Anteprima cartello</Tasto> e stampa. Il
          QR porta alla pagina di accesso dell'azienda, non contiene PIN né dati — se
          finisce in mani sbagliate, senza un PIN non ci si fa niente.
        </FAQ>
      </div>

      <div style={{
        marginTop: 40, padding: '18px 20px', background: LT.verdeSoft,
        borderRadius: LT.r.md, display: 'flex', gap: 14, alignItems: 'flex-start',
      }}>
        <Ico d={I.check} size={20} stroke={LT.verde} style={{ marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, color: LT.verde, fontSize: 14, marginBottom: 4 }}>
            Fine del percorso HACCP
          </div>
          <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.6 }}>
            Con questa guida il blocco HACCP è completo: dai primi passi alla
            configurazione, dal lavoro di ogni giorno alle stampe per l'ispezione. Per il
            modulo Tracciabilità, il percorso riparte da{' '}
            <Strong>Primi passi con la Tracciabilità</Strong>.
          </div>
        </div>
      </div>

    </GuidaLayout>
  );
}
